import { Spinner } from '@/components/ui';
import { CommentItem } from '@/components/features/CommentItem/CommentItem';
import { CommentInput } from '@/components/features/CommentInput/CommentInput';
import { useComments, useCreateComment, useUpdateComment, useDeleteComment } from '@/api';
import { useAuthStore } from '@/stores';

import type { Comment } from '@/types/poll';

import styles from './CommentThread.module.css';

interface CommentThreadProps {
    pollSlug: string;
}

export function CommentThread({ pollSlug }: CommentThreadProps) {
    const { isLogged } = useAuthStore();
    const {
        data,
        isLoading,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
    } = useComments(pollSlug);
    const createComment = useCreateComment(pollSlug);
    const updateComment = useUpdateComment(pollSlug);
    const deleteComment = useDeleteComment(pollSlug);

    const comments = data?.pages.flatMap((page) => page.results) ?? [];

    const handleAddComment = (text: string) => {
        createComment.mutate({ text });
    };

    const handleReply = (text: string, parentId: number) => {
        createComment.mutate({ text, parent_id: parentId });
    };

    const handleEdit = (commentId: number, text: string) => {
        updateComment.mutate({ commentId, payload: { text } });
    };

    const handleDelete = (commentId: number) => {
        deleteComment.mutate(commentId);
    };

    const threadedComments = comments.length > 0 ? buildThreads(comments) : [];

    return (
        <div className={styles.container}>
            <h3 className={styles.title}>Comentarios</h3>

            {isLogged && (
                <div className={styles.inputWrapper}>
                    <CommentInput
                        onSubmit={handleAddComment}
                        isLoading={createComment.isPending}
                    />
                </div>
            )}

            {isLoading ? (
                <div className={styles.loading}>
                    <Spinner size="md" />
                </div>
            ) : threadedComments.length === 0 ? (
                <p className={styles.empty}>Sin comentarios aun. Se el primero.</p>
            ) : (
                <>
                    <div className={styles.list}>
                        {threadedComments.map((comment) => (
                            <CommentItem
                                key={comment.id}
                                comment={comment}
                                onReply={handleReply}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                            />
                        ))}
                    </div>

                    {hasNextPage && (
                        <div className={styles.loadMore}>
                            {isFetchingNextPage ? (
                                <span className={styles.loadMoreText}>Cargando...</span>
                            ) : (
                                <button
                                    className={styles.loadMoreBtn}
                                    onClick={() => fetchNextPage()}
                                    type="button"
                                >
                                    Cargar mas comentarios
                                </button>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function buildThreads(comments: Comment[]): Comment[] {
    const map = new Map<number, Comment>();
    const roots: Comment[] = [];

    for (const comment of comments) {
        map.set(comment.id, { ...comment, replies: [] });
    }

    for (const comment of comments) {
        const node = map.get(comment.id)!;
        if (comment.parent && map.has(comment.parent)) {
            map.get(comment.parent)!.replies!.push(node);
        } else {
            roots.push(node);
        }
    }

    return roots;
}
